import { Component, OnInit, OnDestroy, ChangeDetectorRef, NgZone, inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { LucideAngularModule, Play, ExternalLink, Clock, ChevronRight, Globe } from 'lucide-angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ModalService } from '../services/modal.service';

interface Platform {
  name: string;
  logo: string;
  color: string;
  url: string;
  invertLogo: boolean;
  isHighlighted?: boolean;
}

interface Project {
  slug: string;
  artist: string;
  title: string;
  type: string;
  coverUrl: string;
  isAvailable: boolean;
  releaseDate?: Date;
  platforms: Platform[];
}

@Component({
  selector: 'app-project',
  standalone: true,
  imports: [CommonModule, LucideAngularModule, RouterLink, TranslateModule],
  template: `
    <div *ngIf="project" class="min-h-screen bg-dark-100 font-satoshi text-white relative overflow-hidden flex flex-col">
      
      <!-- Blurred BG with dual layers -->
      <div 
        class="fixed inset-0 bg-cover bg-center scale-150 blur-[100px] opacity-20 pointer-events-none animate-slow-pulse"
        [style.backgroundImage]="'url(' + project.coverUrl + ')'">
      </div>
      <div class="fixed inset-0 bg-gradient-to-b from-dark-100/60 via-dark-100/40 to-dark-100 pointer-events-none"></div>
      
      <!-- Floating particles -->
      <div class="fixed inset-0 pointer-events-none overflow-hidden z-[1]">
        <div class="absolute w-1 h-1 bg-white/20 rounded-full animate-float-1" style="top:20%;left:15%"></div>
        <div class="absolute w-1.5 h-1.5 bg-primary/30 rounded-full animate-float-2" style="top:60%;left:80%"></div>
        <div class="absolute w-1 h-1 bg-white/15 rounded-full animate-float-3" style="top:40%;left:50%"></div>
        <div class="absolute w-0.5 h-0.5 bg-white/25 rounded-full animate-float-1" style="top:75%;left:25%"></div>
        <div class="absolute w-1 h-1 bg-primary/20 rounded-full animate-float-2" style="top:30%;left:70%"></div>
        <div class="absolute w-0.5 h-0.5 bg-white/20 rounded-full animate-float-3" style="top:85%;left:60%"></div>
      </div>

      <!-- Top bar -->
      <header class="relative z-20 w-full px-5 py-4 flex items-center justify-between max-w-3xl mx-auto">
        <a routerLink="/" class="flex items-center gap-2 opacity-70 hover:opacity-100 transition-opacity">
          <img src="logo.png" alt="COLORFOL" class="h-5" />
        </a>
        <div class="flex items-center gap-3">
          <span class="text-[10px] text-gray-500 font-medium uppercase tracking-widest">{{ project.type }}</span>
          <button (click)="toggleLang()" class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-white/[0.08] hover:border-white/20 bg-white/[0.03] hover:bg-white/[0.06] transition-all text-[10px] font-bold text-gray-400 hover:text-white uppercase tracking-wider">
            <lucide-icon [img]="Globe" class="w-3 h-3"></lucide-icon>
            {{ currentLang }}
          </button>
        </div>
      </header>

      <!-- Main content -->
      <main class="relative z-10 flex-1 flex flex-col items-center justify-center w-full max-w-3xl mx-auto px-5 py-8">
        
        <div class="w-full flex flex-col md:flex-row items-center md:items-start gap-2 md:gap-12">
          
          <!-- Cover Art with reflection -->
          <div class="relative flex-shrink-0 group flex flex-col items-center">
            <!-- Animated glow ring -->
            <div 
              class="absolute -inset-8 rounded-3xl blur-[60px] opacity-20 pointer-events-none transition-opacity duration-700 group-hover:opacity-40 animate-glow-pulse"
              [style.background]="'url(' + project.coverUrl + ') center/cover'">
            </div>
            
            <!-- Main cover -->
            <div class="relative w-64 h-64 md:w-80 md:h-80 rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10 group-hover:ring-white/20 transition-all duration-500 group-hover:shadow-[0_25px_80px_-15px_rgba(255,86,32,0.2)]">
              <img [src]="project.coverUrl" [alt]="project.title" class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              
              <div *ngIf="project.isAvailable" 
                class="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer">
                <div class="w-16 h-16 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-xl scale-75 group-hover:scale-100 transition-transform duration-300">
                  <lucide-icon [img]="Play" class="w-7 h-7 text-dark-100 ml-0.5"></lucide-icon>
                </div>
              </div>
            </div>
            
            <!-- Reflection effect -->
            <div class="w-64 md:w-80 h-10 md:h-16 mt-1 rounded-b-2xl overflow-hidden opacity-20 pointer-events-none" style="transform: scaleY(-1)">
              <img [src]="project.coverUrl" [alt]="''" class="w-full h-full object-cover object-bottom" style="mask-image: linear-gradient(to bottom, rgba(0,0,0,0.4), transparent); -webkit-mask-image: linear-gradient(to bottom, rgba(0,0,0,0.4), transparent);" />
            </div>
          </div>

          <!-- Right side -->
          <div class="flex-1 flex flex-col items-center md:items-start text-center md:text-left w-full min-w-0 md:pt-4">
            
            <p class="text-lg md:text-xl text-gray-300 font-bold mb-1">{{ project.artist }}</p>
            <h1 class="text-3xl md:text-4xl font-black mb-6 leading-tight tracking-tight">{{ project.title }}</h1>
            
            <!-- ===== AVAILABLE STATE ===== -->
            <div *ngIf="project.isAvailable" class="w-full">
              
              <!-- COLORFOL highlight CTA -->
              <a [href]="project.platforms[0].url" target="_blank" rel="noopener"
                class="group/cta flex items-center gap-4 w-full px-5 py-4 rounded-2xl bg-gradient-to-r from-primary/15 via-primary/10 to-primary/5 border border-primary/20 hover:border-primary/40 backdrop-blur-sm transition-all duration-300 mb-4 hover:shadow-[0_0_30px_-5px_rgba(255,86,32,0.15)]">
                <div class="w-12 h-12 rounded-xl bg-white flex items-center justify-center flex-shrink-0 shadow-lg shadow-primary/20 group-hover/cta:scale-110 transition-transform p-2.5">
                  <img src="other/icon/colorfol_logo.png" alt="COLORFOL" class="max-h-full max-w-full object-contain" />
                </div>
                <div class="flex-1 text-left">
                  <span class="text-[15px] font-black text-white block">{{ 'PROJECT.COLORFOL_CTA' | translate }}&nbsp;<span class="text-primary">Colorfol</span></span>
                </div>
                <div class="w-9 h-9 rounded-xl bg-primary/20 group-hover/cta:bg-primary flex items-center justify-center flex-shrink-0 transition-all duration-300">
                  <lucide-icon [img]="ChevronRight" class="w-4 h-4 text-primary group-hover/cta:text-white transition-colors"></lucide-icon>
                </div>
              </a>

              <!-- Separator -->
              <div class="flex items-center gap-3 mb-4">
                <div class="flex-1 h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent"></div>
                <span class="text-[10px] text-gray-600 font-bold uppercase tracking-widest">{{ 'PROJECT.ALSO_ON' | translate }}</span>
                <div class="flex-1 h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent"></div>
              </div>

              <!-- Other platforms grid -->
              <div class="flex flex-col gap-2.5 w-full">
                <a *ngFor="let p of getOtherPlatforms()" 
                  [href]="p.url" target="_blank" rel="noopener"
                  class="group/btn flex items-center gap-4 rounded-2xl px-4 py-3.5 bg-gradient-to-r from-white/[0.04] to-white/[0.02] hover:from-white/[0.07] hover:to-white/[0.04] border border-white/[0.06] hover:border-white/[0.12] backdrop-blur-md transition-all duration-300 cursor-pointer">
                  <div class="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 p-2" [style.background]="'linear-gradient(135deg, ' + p.color + '22, ' + p.color + '08)'">
                    <img [src]="p.logo" [alt]="p.name.includes('.') ? (p.name | translate) : p.name" class="max-h-full max-w-full object-contain" [class.invert]="p.invertLogo" />
                  </div>
                  <span class="text-[15px] font-bold text-white/90 group-hover/btn:text-white transition-colors truncate">
                    {{ p.name.includes('.') ? (p.name | translate) : p.name }}
                  </span>
                  <div class="ml-auto flex-shrink-0 w-8 h-8 rounded-lg bg-white/[0.04] group-hover/btn:bg-white/[0.08] flex items-center justify-center transition-all duration-300 group-hover/btn:translate-x-0.5">
                    <lucide-icon [img]="ChevronRight" class="w-4 h-4 text-gray-500 group-hover/btn:text-white transition-colors"></lucide-icon>
                  </div>
                </a>
              </div>
            </div>

            <!-- ===== COMING SOON STATE ===== -->
            <div *ngIf="!project.isAvailable" class="w-full mt-6">
              <p class="text-xs text-gray-500 font-medium mb-6">{{ 'PROJECT.NOT_AVAILABLE' | translate }}</p>
              
              <div class="bg-white/[0.04] backdrop-blur-sm border border-white/[0.08] rounded-2xl p-6 w-full">
                <div class="flex items-center gap-2 mb-5">
                  <lucide-icon [img]="Clock" class="w-4 h-4 text-primary"></lucide-icon>
                  <span class="text-xs font-bold text-primary uppercase tracking-widest">Coming Soon</span>
                </div>
                
                <div class="grid grid-cols-4 gap-3">
                  <div class="text-center">
                    <div class="bg-dark-100 border border-white/[0.08] rounded-xl py-3">
                      <span class="text-2xl md:text-3xl font-black tabular-nums">{{ countdown.days }}</span>
                    </div>
                    <span class="text-[9px] font-bold text-gray-500 uppercase mt-1.5 block">{{ 'PROJECT.DAYS' | translate }}</span>
                  </div>
                  <div class="text-center">
                    <div class="bg-dark-100 border border-white/[0.08] rounded-xl py-3">
                      <span class="text-2xl md:text-3xl font-black tabular-nums">{{ countdown.hours }}</span>
                    </div>
                    <span class="text-[9px] font-bold text-gray-500 uppercase mt-1.5 block">{{ 'PROJECT.HOURS' | translate }}</span>
                  </div>
                  <div class="text-center">
                    <div class="bg-dark-100 border border-white/[0.08] rounded-xl py-3">
                      <span class="text-2xl md:text-3xl font-black tabular-nums">{{ countdown.minutes }}</span>
                    </div>
                    <span class="text-[9px] font-bold text-gray-500 uppercase mt-1.5 block">Min</span>
                  </div>
                  <div class="text-center">
                    <div class="bg-dark-100 border border-white/[0.08] rounded-xl py-3">
                      <span class="text-2xl md:text-3xl font-black tabular-nums">{{ countdown.seconds }}</span>
                    </div>
                    <span class="text-[9px] font-bold text-gray-500 uppercase mt-1.5 block">Sec</span>
                  </div>
                </div>
              </div>

              <button (click)="modalService.openComingSoon($event)" class="w-full mt-4 py-3.5 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold rounded-xl flex items-center justify-center gap-2">
                {{ 'PROJECT.PREREGISTER' | translate }}
                <lucide-icon [img]="ChevronRight" class="w-4 h-4"></lucide-icon>
              </button>
            </div>

          </div>
        </div>

      </main>

      <!-- Footer -->
      <footer class="relative z-10 py-8 flex flex-col items-center gap-3 text-center">
        <div class="flex items-center gap-2">
          <span class="text-[9px] text-gray-600 font-bold uppercase tracking-widest">Powered by</span>
          <img src="logo.png" alt="Colorfol" class="h-3 opacity-60" />
        </div>
        <p class="text-[9px] text-gray-700">© {{ currentYear }} Colorfol. All rights reserved.</p>
      </footer>
      
    </div>
  `,
  styles: [`
    @keyframes float-1 {
      0%, 100% { transform: translateY(0) translateX(0); opacity: 0.3; }
      50% { transform: translateY(-30px) translateX(10px); opacity: 0.7; }
    }
    @keyframes float-2 {
      0%, 100% { transform: translateY(0) translateX(0); opacity: 0.2; }
      50% { transform: translateY(20px) translateX(-15px); opacity: 0.6; }
    }
    @keyframes float-3 {
      0%, 100% { transform: translateY(0) translateX(0); opacity: 0.4; }
      50% { transform: translateY(-20px) translateX(-8px); opacity: 0.2; }
    }
    @keyframes slow-pulse {
      0%, 100% { opacity: 0.15; transform: scale(1.5); }
      50% { opacity: 0.25; transform: scale(1.55); }
    }
    @keyframes glow-pulse {
      0%, 100% { opacity: 0.2; }
      50% { opacity: 0.35; }
    }
    :host {
      display: block;
    }
    .animate-float-1 { animation: float-1 6s ease-in-out infinite; }
    .animate-float-2 { animation: float-2 8s ease-in-out infinite; }
    .animate-float-3 { animation: float-3 7s ease-in-out infinite; }
    .animate-slow-pulse { animation: slow-pulse 8s ease-in-out infinite; }
    .animate-glow-pulse { animation: glow-pulse 4s ease-in-out infinite; }
  `]
})
export class ProjectComponent implements OnInit, OnDestroy {
  modalService = inject(ModalService);
  project: Project | null = null;
  countdown = { days: '00', hours: '00', minutes: '00', seconds: '00' };
  currentYear = new Date().getFullYear();
  private timerInterval: any;

  readonly Play = Play;
  readonly ExternalLink = ExternalLink;
  readonly Clock = Clock;
  readonly ChevronRight = ChevronRight;
  readonly Globe = Globe;
  currentLang: string = 'EN';

  private mockProjects: Record<string, Project> = {
    'gros-nerfs': {
      slug: 'gros-nerfs',
      artist: 'Tenor',
      title: 'Gros Nerfs',
      type: 'Album',
      coverUrl: 'covers/gros-nerfs.png',
      isAvailable: true,
      platforms: [
        { name: 'COLORFOL', logo: 'logo.png', color: '#FF5620', url: '#', invertLogo: false, isHighlighted: true },
        { name: 'Spotify', logo: 'other/icon/Spotify_logo_without_text.svg.png', color: '#1DB954', url: '#', invertLogo: false },
        { name: 'Apple Music', logo: 'other/icon/Apple_Music_icon.svg.png', color: '#FA243C', url: '#', invertLogo: false },
        { name: 'Deezer', logo: 'other/icon/DEEZER.PA-dbdcf2cf.png', color: '#A238FF', url: '#', invertLogo: false },
        { name: 'YouTube Music', logo: 'other/icon/Youtube_Music_icon.svg.png', color: '#FF0000', url: '#', invertLogo: false },
        { name: 'Boomplay', logo: 'other/icon/BOOMPLAY.png', color: '#663399', url: '#', invertLogo: false },
        { name: 'Audiomack', logo: 'other/icon/audiomack.png', color: '#FFA500', url: '#', invertLogo: false }
      ]
    },
    'vogue-merry': {
      slug: 'vogue-merry',
      artist: '2Nilame',
      title: 'Vogue Merry',
      type: 'Album',
      coverUrl: 'covers/vogue-merry.png',
      isAvailable: true,
      platforms: [
        { name: 'COLORFOL', logo: 'logo.png', color: '#FF5620', url: 'https://store.colorfol.com/album/3610', invertLogo: false, isHighlighted: true },
        { name: 'Spotify', logo: 'other/icon/Spotify_logo_without_text.svg.png', color: '#1DB954', url: 'https://open.spotify.com/intl-fr/album/22yrgQ0a00eZGUbv3kszlK', invertLogo: false },
        { name: 'Apple Music', logo: 'other/icon/Apple_Music_icon.svg.png', color: '#FA243C', url: 'https://geo.music.apple.com/au/album/vogue-merry/6773891072?app=music&ls=1', invertLogo: false },
        { name: 'PROJECT.DOWNLOAD', logo: 'other/icon/ITunes_12.2_logo.png', color: '#EA4CC0', url: '#', invertLogo: false },
        { name: 'Amazon Music', logo: 'other/icon/Amazonmusic.logo.png', color: '#00A8E1', url: 'https://music.amazon.fr/albums/B0H34S9PP5?marketplaceId=A13V1IB3VIYZZH&musicTerritory=FR&ref=dm_sh_eNTXLagYWYBDsjMqh1AwxZxAT', invertLogo: false },
        { name: 'YouTube Music', logo: 'other/icon/Youtube_Music_icon.svg.png', color: '#FF0000', url: 'https://www.youtube.com/playlist?list=OLAK5uy_lLA4Ve5Nn1w5jeGz0DPwVg-4hDEiWncSA', invertLogo: false },
        { name: 'Tidal', logo: 'other/icon/tidal-logo-png-transparent-png.png', color: '#000000', url: 'http://www.tidal.com/album/528497364', invertLogo: true }
      ]
    }
  };

  constructor(
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private translate: TranslateService,
    private meta: Meta,
    private titleService: Title
  ) {
    this.currentLang = this.translate.currentLang?.toUpperCase() || 'EN';
  }

  ngOnInit() {
    this.route.paramMap.subscribe(params => {
      const slug = params.get('slug');
      if (slug && this.mockProjects[slug]) {
        this.project = this.mockProjects[slug];
      } else {
        this.project = {
          slug: slug || 'unknown',
          artist: 'Artiste',
          title: slug?.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Projet',
          type: 'Single',
          coverUrl: 'covers/gros-nerfs.png',
          isAvailable: true,
          platforms: [
            { name: 'COLORFOL', logo: 'logo.png', color: '#FF5620', url: '#', invertLogo: false, isHighlighted: true },
            { name: 'Spotify', logo: 'other/icon/Spotify_logo_without_text.svg.png', color: '#1DB954', url: '#', invertLogo: false },
            { name: 'Apple Music', logo: 'other/icon/Apple_Music_icon.svg.png', color: '#FA243C', url: '#', invertLogo: false }
          ]
        };
      }

      if (this.project && !this.project.isAvailable && this.project.releaseDate) {
        this.startCountdown();
      }

      this.updateMetaTags();
    });
  }

  private updateMetaTags() {
    if (!this.project) return;
    
    const pageTitle = `${this.project.artist} - ${this.project.title} | COLORFOL`;
    const description = `Écoutez ${this.project.title} par ${this.project.artist} sur Colorfol et toutes vos plateformes préférées.`;
    const imageUrl = `https://distrib.colorfol.com/${this.project.coverUrl}`;
    const url = `https://distrib.colorfol.com/${this.project.slug}`;

    this.titleService.setTitle(pageTitle);

    this.meta.updateTag({ name: 'description', content: description });
    
    // Open Graph / Facebook / WhatsApp
    this.meta.updateTag({ property: 'og:type', content: 'music.album' });
    this.meta.updateTag({ property: 'og:title', content: pageTitle });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:image', content: imageUrl });
    this.meta.updateTag({ property: 'og:url', content: url });
    
    // Twitter
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: pageTitle });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image', content: imageUrl });
  }

  ngOnDestroy() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  toggleLang() {
    const newLang = this.translate.currentLang === 'fr' ? 'en' : 'fr';
    this.translate.use(newLang);
    this.currentLang = newLang.toUpperCase();
  }

  getOtherPlatforms(): Platform[] {
    return this.project?.platforms.slice(1) || [];
  }

  private startCountdown() {
    this.updateCountdown();
    const platformId = inject(PLATFORM_ID);
    if (isPlatformBrowser(platformId)) {
      this.ngZone.runOutsideAngular(() => {
        this.timerInterval = setInterval(() => {
        this.ngZone.run(() => {
          this.updateCountdown();
          this.cdr.detectChanges();
        });
      }, 1000);
    });
    }
  }

  private updateCountdown() {
    if (!this.project?.releaseDate) return;
    const now = new Date().getTime();
    const target = this.project.releaseDate.getTime();
    const diff = Math.max(0, target - now);

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    this.countdown = {
      days: days.toString().padStart(2, '0'),
      hours: hours.toString().padStart(2, '0'),
      minutes: minutes.toString().padStart(2, '0'),
      seconds: seconds.toString().padStart(2, '0')
    };
  }
}
